import React from 'react';
import { FormFieldConfig } from 'src/Library/Types';

interface IProps {
    field: FormFieldConfig;
    children: React.ReactNode;
    extractedFields?: Set<string>;
    labelMap?: Record<string, string>;
}

const FormFieldRenderer = ({ field, children, extractedFields = new Set(),labelMap }: IProps) => {

     const findExactMatch = (node: React.ReactNode, id: string) => {
        const childrenArray = React.Children.toArray(node);
        for (const child of childrenArray) {
            if (React.isValidElement(child)) {
                if (child.props.id === id) return child;
                if (child.props.children) {
                const found = findExactMatch(child.props.children, id);
                if (found) return found;
                }
            }
        }
        return null;
    };

    const findMatchingChild = (node: React.ReactNode, targetId: string): React.ReactElement | null => {

        if (React.isValidElement(node) && node.props.id === targetId) {
            return node;
        }
        // Parse hierarchical ID (e.g., "grid.additionalDetails.movementType")
        const idParts = targetId.split('.');
        if (idParts.length > 1) {
            const parentId = idParts.slice(0, -1).join('.');
            const childId = idParts[idParts.length - 1];
            
            extractedFields.add(targetId);

            const parentContainer = findExactMatch(node, parentId);
            if (parentContainer) {
                // Search for child within parent container
                return findExactMatch(parentContainer.props.children, childId);
            }
        }
        const childrenArray = React.Children.toArray(node)

        for (const child of childrenArray){
            if (React.isValidElement(child)){
                if (child.props.id === targetId){
                    return child
                }

                if (child.props.children){
                    const foundChild = findMatchingChild(child.props.children, targetId);

                    if (foundChild) {
                        return foundChild;
                    }
                }
            }
        }
        return null;
    }

    const removeExtractedChildren = (element: React.ReactElement): React.ReactElement => {
        if (!element.props.children) return element

        const filteredChildren = React.Children.toArray(element.props.children).filter((child) => {
            if (React.isValidElement(child) && child.props.id){
                const extractedArray = Array.from(extractedFields)
                for (const extractedId of extractedArray) {
                    const parts = extractedId.split('.');
                    if (parts.length > 1 && parts[parts.length - 1] === child.props.id) {
                        return false; // Skip this child
                    }
                }
            }
            return true; // Keep this child
        }).map((child) => {
            if (React.isValidElement(child) && child.props.children){
                return removeExtractedChildren(child)
            }
            return child
        }) 

        return React.cloneElement(element, element.props, ...filteredChildren);
    }

    const matchingChild = findMatchingChild(children, field.id)

    if (matchingChild && React.isValidElement(matchingChild)) {
        const cleanedChild = removeExtractedChildren(matchingChild);
        const newProps = Object.assign({}, cleanedChild.props, {
            "data-grid-pos": `${field.posX}-${field.posY}`,
            "data-colspan": field.colspan || 1,
            "__labelMap": labelMap
        })
        return React.cloneElement(cleanedChild, newProps);
    }

    return null
}

export default FormFieldRenderer